import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ChatRepository } from 'repository/chatRepository';
import './ChatList.css';

// 커스텀 아바타 컴포넌트
const CustomAvatar = ({ name }: { name: string }) => {
    const initial = name ? name.charAt(0).toUpperCase() : '?';
    const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
    ];
    const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;

    return (
        <div
            className="custom-avatar"
            style={{ backgroundColor: colors[colorIndex] }}
        >
            {initial}
        </div>
    );
};

interface ChatRoom {
    roomId: string;
    roomName: string;
    roomType: 'MEETING' | 'DIRECT';
    lastMessage?: string;
    lastMessageTime?: string;
    unreadCount: number;
    participantCount: number;
    meetingId?: string;
    thumbnailUrl?: string;
}

export const ChatList = () => {
    const navigate = useNavigate();
    const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const currentUserId = sessionStorage.getItem('userId') || 'mock-user-001';

    // 채팅방 목록 조회
    useEffect(() => {
        const loadChatRooms = async () => {
            setIsLoading(true);
            try {
                const response = await ChatRepository.getRoomList(currentUserId);
                if (response?.data?.list) {
                    setChatRooms(response.data.list);
                }
            } catch (error) {
                console.error('채팅방 목록 조회 실패:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadChatRooms();
    }, [currentUserId]);

    // 검색 필터링
    const filteredRooms = chatRooms.filter(room =>
        room.roomName.toLowerCase().includes(searchValue.toLowerCase())
    );

    // 시간 포맷
    const formatLastTime = (timeStr?: string) => {
        if (!timeStr) return '';

        const date = new Date(timeStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 7) {
            return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
        } else if (diffDays > 1) {
            return `${diffDays}일 전`;
        } else if (diffDays === 1) {
            return '어제';
        } else if (diffHours > 0) {
            return `${diffHours}시간 전`;
        } else if (diffMins > 0) {
            return `${diffMins}분 전`;
        } else {
            return '방금 전';
        }
    };

    const handleRoomClick = (roomId: string) => {
        navigate(`/chat/room/${roomId}`);
    };

    if (isLoading) {
        return (
            <div className="chat-list-loading">
                <div className="loading-spinner"></div>
                <p>채팅방 목록 로딩 중...</p>
            </div>
        );
    }

    return (
        <div className="chat-list-wrapper">
            <div className="chat-list-header">
                <h1>채팅</h1>
            </div>

            <div className="chat-search-wrapper">
                <input
                    type="text"
                    className="chat-search-input"
                    placeholder="채팅방 검색..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                />
                {searchValue && (
                    <button
                        className="chat-search-clear"
                        onClick={() => setSearchValue('')}
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className="chat-room-list">
                {filteredRooms.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">💬</div>
                        <div className="empty-title">
                            {searchValue ? '검색 결과가 없어요' : '아직 채팅방이 없어요'}
                        </div>
                        <div className="empty-description">
                            {searchValue ? '다른 검색어로 시도해 보세요' : '모임에 참여하면 채팅을 시작할 수 있어요'}
                        </div>
                    </div>
                ) : (
                    filteredRooms.map(room => (
                        <div
                            key={room.roomId}
                            className="chat-room-item"
                            onClick={() => handleRoomClick(room.roomId)}
                        >
                            <CustomAvatar name={room.roomName} />
                            <div className="chat-room-info">
                                <div className="chat-room-header">
                                    <span className="chat-room-name">{room.roomName}</span>
                                    <span className="chat-room-time">{formatLastTime(room.lastMessageTime)}</span>
                                </div>
                                <div className="chat-room-preview">
                                    <span className="chat-room-message">{room.lastMessage || '새로운 채팅방'}</span>
                                    {room.unreadCount > 0 && (
                                        <span className="chat-room-badge">{room.unreadCount}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
